import api from "./axiosConfig";
import axios from "axios";
import { CreditAnalysis } from "../types";

export interface AnalyzeCreditReportRequest {
  clientId: string;
  creditReportJson: string;
}

export interface AnalyzeCreditReportResponse {
  clientId: string;
  actionPlanHeadline: string;
  actionPlanStepsJson: string;
  complianceNotes: string;
  fullAnalysisJson: string;
  createdAt: string;
}

export interface ChatHistoryMessage {
  role: string;
  content: string;
}

export interface ChatRequest {
  clientId?: string | null;
  message: string;
  history?: ChatHistoryMessage[];
}

export interface ChatResponse {
  clientId?: string | null;
  message: string;
  response: string;
}

export interface GenerateDisputeReasonPayload {
  creditorName: string;
  accountType: string;
  accountNumber?: string;
  balance?: number | null;
  status: string;
  bureaus?: string | null;
  additionalContext?: string | null;
}

export interface GenerateDisputeReasonResult {
  reason: string;
}

export interface CreditReportParseScores {
  equifax?: number | null;
  experian?: number | null;
  transUnion?: number | null;
}

export interface ParsedTradelineDto {
  creditorName: string;
  accountNumber: string;
  accountType: string;
  balance: number;
  status: string;
  dateOpened?: string | null;
  dateReported?: string | null;
  isNegative: boolean;
  bureaus: string[];
  remarks?: string | null;
  selected?: boolean;
}

export interface ParsedInquiryDto {
  creditorName: string;
  date?: string | null;
  bureau: string;
  isHard: boolean;
}

export interface ParsedSummaryDto {
  totalAccounts: number;
  negativeItems: number;
  collections: number;
  latePayments: number;
  inquiries: number;
  recommendedActions: string[];
}

export interface CreditReportParseResultDto {
  scores?: CreditReportParseScores | null;
  tradelines: ParsedTradelineDto[];
  inquiries: ParsedInquiryDto[];
  summary?: ParsedSummaryDto | null;
}

export interface CreditReportParseResponseDto {
  documentId: string;
  clientId: string;
  parsed: CreditReportParseResultDto;
  rawTextLength: number;
}

export interface ApplyParsedReportPayload {
  clientId: string;
  scores?: CreditReportParseScores | null;
  tradelines?: ParsedTradelineDto[];
}

export interface ApplyParsedReportResultDto {
  message: string;
  scoresUpdated: boolean;
  disputeItemsCreated: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string | null;
  errors?: string[] | null;
}

function unwrap<T>(payload: ApiEnvelope<T>): T {
  if (!payload.success || payload.data === undefined) {
    const msg = payload.message || "Request failed";
    throw new Error(msg);
  }
  return payload.data;
}

export function getAiApiErrorMessage(err: unknown, fallback = "Request failed"): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiEnvelope<unknown> | undefined;
    if (body?.message) {
      return body.message;
    }
    return err.message || fallback;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

/** Latest analysis payload from API. */
interface LatestAnalysisDto {
  id: string;
  tenantId: string;
  clientId: string;
  equifaxScore?: number;
  experianScore?: number;
  transUnionScore?: number;
  totalNegativeItems?: number;
  priorityLevel?: string;
  estimatedScoreImprovementLow?: number;
  estimatedScoreImprovementHigh?: number;
  analystNote?: string;
  actionPlanHeadline: string;
  actionPlanStepsJson: string;
  timelineEstimate: string;
  fullAnalysisJson: string;
  createdAt: string;
}

function toCreditAnalysis(d: LatestAnalysisDto): CreditAnalysis {
  return {
    id: d.id,
    tenantId: d.tenantId,
    clientId: d.clientId,
    equifaxScore: d.equifaxScore ?? 0,
    experianScore: d.experianScore ?? 0,
    transUnionScore: d.transUnionScore ?? 0,
    totalNegativeItems: d.totalNegativeItems ?? 0,
    priorityLevel: d.priorityLevel?.trim() ? d.priorityLevel : "Medium",
    estimatedScoreImprovementLow: d.estimatedScoreImprovementLow ?? 0,
    estimatedScoreImprovementHigh: d.estimatedScoreImprovementHigh ?? 0,
    analystNote: d.analystNote ?? "",
    actionPlanHeadline: d.actionPlanHeadline,
    actionPlanStepsJson: d.actionPlanStepsJson,
    timelineEstimate: d.timelineEstimate,
    fullAnalysisJson: d.fullAnalysisJson,
    createdAt: d.createdAt,
  };
}

export const aiApi = {
  analyzeCreditReport: async (
    payload: AnalyzeCreditReportRequest
  ): Promise<AnalyzeCreditReportResponse> => {
    const response = await api.post<ApiEnvelope<AnalyzeCreditReportResponse>>("/ai/analyze", payload);
    return unwrap(response.data);
  },

  chat: async (payload: ChatRequest): Promise<ChatResponse> => {
    const response = await api.post<ApiEnvelope<ChatResponse>>("/ai/chat", payload);
    return unwrap(response.data);
  },

  getLatestAnalysis: async (clientId: string): Promise<CreditAnalysis> => {
    const response = await api.get<ApiEnvelope<LatestAnalysisDto>>(`/ai/analysis/${clientId}`);
    return toCreditAnalysis(unwrap(response.data));
  },

  generateDisputeReason: async (payload: GenerateDisputeReasonPayload): Promise<GenerateDisputeReasonResult> => {
    const response = await api.post<ApiEnvelope<GenerateDisputeReasonResult>>(
      "/ai/generate-dispute-reason",
      payload
    );
    return unwrap(response.data);
  },

  parseCreditReport: async (documentId: string): Promise<CreditReportParseResponseDto> => {
    const response = await api.post<ApiEnvelope<CreditReportParseResponseDto>>(
      `/ai/parse-credit-report/${documentId}`
    );
    return unwrap(response.data);
  },

  applyParsedReport: async (payload: ApplyParsedReportPayload): Promise<ApplyParsedReportResultDto> => {
    const response = await api.post<ApiEnvelope<ApplyParsedReportResultDto>>(
      "/ai/apply-parsed-report",
      payload
    );
    return unwrap(response.data);
  },
};
